import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { useTheme } from 'lib/context/ThemeContext';

// Components
import Component, { ComponentBackgroundVariants } from 'src/helpers/Component/Component';
import { CarouselDynamicReviewsTheme } from './CarouselDynamicReviews.theme';
import SingleButton from 'src/helpers/SingleButton/SingleButton';
import Headline from 'src/helpers/Headline/Headline';
import classNames from 'classnames';
import { getEnum } from 'lib/utils/get-enum';
import RenderReviewSlider from './RenderReviewSlider.helper';
import SvgIcon from 'src/helpers/SvgIcon/SvgIcon';
import { parseReviewFilterData, useReviewDetails } from 'lib/reviews/get-reviews-v2';

import { GetStaticComponentProps, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import { ChangeLocation } from 'src/helpers/Location/ChangeLocation';
import { SitecoreIds } from 'lib/constants';
import { conditionParametersFactory } from 'lib/rules/parameters/condition-parameters';
import { ReviewRulesStaticProps } from 'lib/reviews/types';

type paginationStyle = 'numbers' | 'dots';

export type CarouselDynamicReviewsProps =
  Feature.EnterpriseWeb.RenewalByAndersen.Components.Reviews.CarouselDynamicReviews &
    ReviewRulesStaticProps;

const CarouselDynamicReviews = (props: CarouselDynamicReviewsProps) => {
  const style = getEnum<ComponentBackgroundVariants>(props.fields?.backgroundColor) ?? 'gray';
  const { themeData } = useTheme(CarouselDynamicReviewsTheme(style));
  const paginationStyle = getEnum<paginationStyle>(props.fields?.carouselStyle) || 'dots';
  const loop = props.fields?.loop?.value || true;
  const autoplay = props.fields?.autoPlay?.value || false;
  const autoPlayInterval = props.fields?.interval?.value || 2000;
  const numberOfReviews = props.fields?.numberOfReviews?.value ?? 2;
  const reviewFilters = props.fields?.reviewFilters?.value;
  const useUserAffiliate =
    reviewFilters?.includes(
      SitecoreIds.Settings.Rules.Definitions.Elements.RbA_Reviews.UserAffiliate.ItemId
    ) ?? false;
  const reviews = useReviewDetails(
    parseReviewFilterData({
      reviewFilterProps: {
        ...props,
        pageSize: numberOfReviews,
      },
      reviewFilters: props.reviewsFilterRules,
    })
  );

  const sliderSettings = {
    dots: true,
    enableNumberedPagination: paginationStyle == 'numbers',
    slidesToShow: 1,
    infinite: loop,
    autoplay: autoplay,
    autoplaySpeed: autoPlayInterval,
    pauseOnFocus: true,
    pauseOnHover: true,
    dotsClass: 'slick-dots static',
    adaptiveHeight: true,
  };

  return (
    <>
      <div className={themeData.classes.sectionWrapper}>
        <Headline classes={themeData.classes.headlineClass} {...props} />
        {useUserAffiliate && <ChangeLocation />}
      </div>
      <div className={classNames('col-span-12')}>
        <Component
          variant="full"
          dataComponent="reviews/carouseldynamicreviews"
          gap="gap-x-0"
          backgroundVariant={style}
          {...props}
        >
          <SvgIcon icon="quote" className={themeData.classes.quoteClass} aria-hidden="true" />
          <div className={classNames('carousel-slider col-span-12')}>
            <div className="carousel-slider">
              <RenderReviewSlider
                sliderSettings={sliderSettings}
                style={style}
                slidesData={reviews}
              />
            </div>
          </div>
        </Component>
      </div>
      <div className={themeData.classes.sectionWrapper}>
        <SingleButton classes={themeData.classes.buttonClass} {...props} />
      </div>
    </>
  );
};

export const getStaticProps: GetStaticComponentProps = async (rendering, layout, context) => {
  const props: ReviewRulesStaticProps = {};
  const datasource = rendering as CarouselDynamicReviewsProps;

  if (!datasource) {
    return {};
  }

  const rules = await conditionParametersFactory.extend(
    datasource.fields?.reviewFilters?.value,
    context,
    layout
  );

  props.reviewsFilterRules = rules;

  return props;
};

export default withDatasourceCheck()<CarouselDynamicReviewsProps>(CarouselDynamicReviews);
